import Alert from '@risk-smart/themed-cloudscape-components/alert';
import type {
  GroupBy,
  ReportingDataInput,
} from '@risksmart-app/shared/reporting/api/schema';
import { getSharedDatasets } from '@risksmart-app/shared/reporting/datasets';
import type { DataType } from '@risksmart-app/shared/reporting/datasets/types';
import _ from 'lodash';
import type { Ref } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useModulesStore } from 'src/context/moduleContext';
import { useDashboardWidgetSettings } from 'src/context/useDashboardWidgetSettings';
import { combinedFilters } from 'src/pages/custom-datasources/detail/combineFilters';
import {
  parseOptionalTypedCustomDatasource,
  type TypedCustomDatasource,
} from 'src/pages/custom-datasources/types';
import { CustomDatasourceModel } from 'src/pages/custom-datasources/update/customDatasourceModel';
import CustomDatasourcePropertyFilter from 'src/pages/custom-datasources/update/CustomDatasourcePropertyFilter';
import { getFieldFromUniqueId } from 'src/pages/custom-datasources/update/fieldValue';
import { DEFAULT_PAGE_SIZE } from 'src/pages/custom-datasources/update/tablePreferences';
import { buildColumnWidthScope } from 'src/pages/custom-datasources/update/useCustomDatasourceColumnWidths';

import {
  useGetAllFormsCustomisation,
  useGetCustomDatasourceById,
  useGetReportingData,
  useGetTags,
} from '@/hooks/queries';
import { useElementCompact } from '@/hooks/useElementCompact';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { emptyFilterQuery } from '@/utils/collectionUtils';
import { customDatasourceUrl } from '@/utils/urls';

import { CollapsibleFilterPreview } from '../filter-preview/CollapsibleFilterPreview';
import type { WidgetRef } from '../types';
import ConfigureWidgetPanel from '../universal-widget/ConfigureWidgetPanel';
import { isWidgetCompact } from '../universal-widget/widgetCompact';
import { useDashboardFilters } from '../useDashboardStore';
import { useWidgetSettingsHandler } from '../widget-context/useWidgetSettingsHandler';
import { useWidgetContext } from '../widget-context/WidgetContext';
import { WidgetContentErrorBoundary } from '../WidgetContentErrorBoundary';
import {
  getFilterPropertyForCategory,
  withDashboardTagTokens,
} from './categoryClickHandler';
import { CustomDataSourceWidgetSettingsModel } from './CustomDataSourceWidgetSettingsModel';
import { buildDashboardTagFilterGroup } from './dashboardTagFilterGroup';
import {
  type CustomDataSourceWidgetSettings,
  customDataSourceWidgetSettingsSchema,
} from './form/customDataSourceWidgetSettingsSchema';
import { useCustomAttributeLookup } from './useCustomAttributeLookup';
import { useCustomDataSourceWidgetExport } from './useCustomDataSourceWidgetExport';
import { useMapReportDataToSeries } from './useMapReportDataToSeries';
import type { ReportField } from './widget-chart/types';
import { WidgetChart } from './widget-chart/WidgetChart';

export const CustomDataSourceWidget = (
  _props: unknown,
  ref: Ref<WidgetRef>
) => {
  const navigate = useNavigate();
  const { customAttributeSchemaLookup } = useCustomAttributeLookup();

  const { data: formCustomisationData } = useGetAllFormsCustomisation({
    queryArgs: {},
  });
  const { t } = useTranslation(['common'], { keyPrefix: 'customDatasources' });
  const { t: tt } = useTranslation(['common']);
  const [settings, setSettings] =
    useDashboardWidgetSettings<CustomDataSourceWidgetSettings>();
  // Column widths are personal, so they are stored per user against this widget
  // instance rather than in `settings` (which lives on the dashboard, and so would
  // apply to everyone a shared dashboard is shared with). Undefined in preview mode,
  // before the widget is on a dashboard and has an id to belong to.
  const widgetId = useWidgetContext()?.widgetId;
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  useWidgetSettingsHandler(ref, () => setShowSettingsModal(true));

  // Collapse the filter pills into a floating icon when the widget is compact.
  const [contentRef, isCompact] =
    useElementCompact<HTMLDivElement>(isWidgetCompact);
  const { success: validSettings } =
    customDataSourceWidgetSettingsSchema.safeParse(settings);

  const { data: customDatasourceResponse, loading: loadingCustomDatasource } =
    useGetCustomDatasourceById({
      queryArgs: { id: settings?.customDataSourceId ?? '' },
      shouldSkip: !settings?.customDataSourceId,
      fetchPolicy: 'no-cache',
    });
  const selectedCustomDatasource: TypedCustomDatasource | undefined = useMemo(
    () =>
      parseOptionalTypedCustomDatasource(
        customDatasourceResponse?.custom_datasource_by_pk
      ) ?? undefined,
    [customDatasourceResponse?.custom_datasource_by_pk]
  );

  // Clear the stored column layout when the datasource changes to ensure fresh
  // column ordering. Only the column layout is field-dependent — page size, wrap
  // lines, striped rows, density and sticky columns are not, so they survive
  // (CUS-220; clearing tablePreferences wholesale would discard them, and a widget
  // whose only preference is e.g. wrap lines has no contentDisplay to compare).
  useEffect(() => {
    const contentDisplay = settings?.tablePreferences?.contentDisplay;
    if (selectedCustomDatasource && contentDisplay) {
      // Check if the current column layout is compatible with the current datasource
      const currentFieldIds = selectedCustomDatasource.Fields?.map((field) =>
        getFieldFromUniqueId(`${field.dataSourceIndex}|${field.fieldId}`)
      );

      const preferenceFieldIds = contentDisplay.map((item) =>
        getFieldFromUniqueId(item.id)
      );

      // If field structures don't match, clear the layout to force refresh
      const fieldsMatch =
        currentFieldIds?.length === preferenceFieldIds.length &&
        currentFieldIds?.every((field) => {
          return preferenceFieldIds.some(
            (prefField) =>
              prefField &&
              field.fieldId === prefField.fieldId &&
              field.dataSourceIndex === prefField.dataSourceIndex
          );
        });

      if (!fieldsMatch) {
        setSettings({
          ...settings,
          tablePreferences: {
            ...settings?.tablePreferences,
            contentDisplay: undefined,
          },
        });
      }
    }
  }, [selectedCustomDatasource, settings, setSettings]);

  const getFieldByValue = (value: string) => {
    const field = getFieldFromUniqueId(value);

    return selectedCustomDatasource?.Fields?.find(
      (f) =>
        f.fieldId === field.fieldId &&
        f.dataSourceIndex === field.dataSourceIndex
    );
  };

  const [pageIndex, setPageIndex] = useState(0);
  const pageSize =
    settings?.chartType === 'table'
      ? (settings?.tablePreferences?.pageSize ?? DEFAULT_PAGE_SIZE)
      : 100000;
  const x1Field = getFieldByValue(settings?.x1FieldId ?? '');
  const x2Field = getFieldByValue(settings?.x2FieldId ?? '');
  const aggregateField = getFieldByValue(settings?.yFieldId ?? '');
  const groupBy: GroupBy[] = [];
  if (settings?.aggregationType) {
    if (x1Field) {
      groupBy.push({
        field: {
          fieldId: x1Field.fieldId,
          dataSourceIndex: x1Field.dataSourceIndex,
        },
        datePrecision: settings.x1FieldDatePrecision,
      });
    }
    if (x2Field) {
      groupBy.push({
        field: {
          fieldId: x2Field.fieldId,
          dataSourceIndex: x2Field.dataSourceIndex,
        },
      });
    }
  }

  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');
  const modules = useModulesStore((s) => s.modules);
  const customDatasourceModel =
    selectedCustomDatasource && customAttributeSchemaLookup
      ? CustomDatasourceModel(
          selectedCustomDatasource,
          customAttributeSchemaLookup,
          formCustomisationData?.form_field_configuration ?? null,
          formSectionsEnabled,
          modules
        )
      : null;

  // Propagate the dashboard tag filter to the CDS reporting query (CUS-203).
  // Other widget types react to dashboard tag filters; CDS widgets historically
  // did not because they never read the dashboard filter store. The reporting
  // engine matches the inlined `tags` array against tag *names*, so resolve the
  // stored TagTypeIds to names before building the filter, and apply it to the
  // root datasource (index 0) — mirroring single-entity widget semantics.
  const filters = useDashboardFilters();
  // Only resolve tag types when the dashboard actually has a tag filter — most
  // dashboards don't, and this avoids an otherwise-unused tag-types fetch.
  const { data: tagData, loading: tagsLoading } = useGetTags({
    queryArgs: {},
    shouldSkip: filters.tags.length === 0,
  });
  // Guard synchronously off the shared dataset (not the async customDatasource
  // model, which is null until customAttributeSchemaLookup resolves — that would
  // fire the report query once without the tag filter, then refetch). The root
  // datasource must expose a `tags` field or the reporting query would 400 on an
  // unknown filter field.
  const rootDatasourceType = selectedCustomDatasource?.Datasources[0]?.type;
  // Resolve the dashboard's selected tags (stored as TagTypeIds) to names — the
  // reporting engine, and the CDS detail page's property filter, both match tags
  // by name. Empty when the root datasource has no `tags` field.
  const dashboardTagNames = useMemo(() => {
    if (!rootDatasourceType) {
      return [];
    }
    if (!('tags' in getSharedDatasets()[rootDatasourceType].fields)) {
      return [];
    }

    return filters.tags
      .map((id) => tagData?.tag_type.find((t) => t.TagTypeId === id)?.Name)
      .filter((name): name is string => Boolean(name));
  }, [rootDatasourceType, filters.tags, tagData]);
  const dashboardTagFilterGroup = useMemo(
    () => buildDashboardTagFilterGroup(dashboardTagNames),
    [dashboardTagNames]
  );
  // When a tag filter is set, hold the report query while the tag types are
  // still loading — otherwise it fires once with the filter missing (showing the
  // unfiltered set) and only picks it up on a later refetch. Gate on the loading
  // flag, not on `!tagData`, so a failed tag fetch settles (loading → false) and
  // the report still runs rather than hanging on a perpetual skip.
  const awaitingTagResolution = filters.tags.length > 0 && tagsLoading;

  const reportDataDefinition: ReportingDataInput | undefined =
    selectedCustomDatasource
      ? {
          dataSources: selectedCustomDatasource.Datasources,
          limit: pageSize,
          filters: combinedFilters(
            selectedCustomDatasource,
            settings?.filtering,
            dashboardTagFilterGroup
          ),
          fields: settings?.aggregationType
            ? []
            : (selectedCustomDatasource?.Fields ?? []),
          groupBy,
          offset: pageIndex * pageSize,
          aggregateType: settings?.aggregationType,
          aggregateField: aggregateField
            ? {
                fieldId: aggregateField.fieldId,
                dataSourceIndex: aggregateField.dataSourceIndex,
              }
            : undefined,
        }
      : undefined;

  const { data, loading, error } = useGetReportingData({
    queryArgs: reportDataDefinition,
    shouldSkip: !validSettings || awaitingTagResolution,
  });

  const aggregateFieldDefinition = aggregateField
    ? (customDatasourceModel?.getField(
        aggregateField.dataSourceIndex,
        aggregateField.fieldId
      ) ?? null)
    : null;

  const x1FieldDefinition = x1Field
    ? (customDatasourceModel?.getField(
        x1Field.dataSourceIndex,
        x1Field.fieldId
      ) ?? null)
    : null;

  const x2FieldDefinition = x2Field
    ? (customDatasourceModel?.getField(
        x2Field.dataSourceIndex,
        x2Field.fieldId
      ) ?? null)
    : null;

  const xAxisDataType: DataType = x1FieldDefinition?.dataType ?? 'text';
  const seriesData = useMapReportDataToSeries({
    aggregationType: settings?.aggregationType ?? null,
    x1FieldDefinition,
    x2FieldDefinition,
    reportingData: (data?.reportingData as ReportField[][]) ?? [],
    x1GroupByDatePrecision: settings?.x1FieldDatePrecision ?? null,
    // Don't currently support date precision on x2
    x2GroupByDatePrecision: null,
    aggregateFieldDefinition,
  });

  const total = _.sumBy(
    seriesData.flatMap((s) => s.data),
    (val) => val.y ?? 0
  );

  // CSV export for the table variant: per-widget download + full-dashboard zip
  // contribution (RSP-6025).
  useCustomDataSourceWidgetExport({
    chartType: settings?.chartType,
    enabled: Boolean(
      validSettings &&
      !error &&
      selectedCustomDatasource &&
      customAttributeSchemaLookup &&
      reportDataDefinition
    ),
    loading,
    title: settings?.title,
    reportDataDefinition,
    customDatasource: selectedCustomDatasource,
    customAttributeSchemaLookup,
    formFieldConfigurations:
      formCustomisationData?.form_field_configuration ?? null,
    contentDisplay: settings?.tablePreferences?.contentDisplay,
  });

  return (
    <div ref={contentRef} className={'h-full flex flex-col gap-2'}>
      {showSettingsModal && (
        <CustomDataSourceWidgetSettingsModel
          onDismiss={() => setShowSettingsModal(false)}
          onSave={async (data) => {
            const title = data.customTitle
              ? data.title
              : tt('dashboard.widgets.customDataSourceWidget.title');
            setSettings({ ...data, title });
          }}
        />
      )}
      {!validSettings ? (
        <ConfigureWidgetPanel
          onConfigureClick={() => setShowSettingsModal(true)}
        />
      ) : (
        <>
          {!reportDataDefinition && !loadingCustomDatasource && (
            <Alert header={'Error'} type={'warning'}>
              {t('datasource_not_found_message')}
            </Alert>
          )}
        </>
      )}

      {error && (
        <Alert header={'Error'} type={'error'}>
          {t('data_request_failure_message')}
        </Alert>
      )}

      <WidgetContentErrorBoundary
        onOpenSettings={() => setShowSettingsModal(true)}
      >
        {!error &&
          validSettings &&
          settings &&
          customAttributeSchemaLookup &&
          selectedCustomDatasource && (
            <>
              <WidgetChart
                key={`${selectedCustomDatasource.Id}-${settings.chartType}`}
                customAttributeSchemaLookup={customAttributeSchemaLookup}
                formFieldConfigurations={
                  formCustomisationData?.form_field_configuration ?? null
                }
                customDatasource={selectedCustomDatasource}
                onPageChangeClick={(e) => {
                  setPageIndex(e.requestedPageIndex);
                }}
                onCategoryClick={({ value }) => {
                  if (
                    selectedCustomDatasource &&
                    x1Field &&
                    x1FieldDefinition
                  ) {
                    // Carry the dashboard tag filter into the drill-through so
                    // the CDS page's list matches the (tag-filtered) bar.
                    const filtering = withDashboardTagTokens(
                      getFilterPropertyForCategory({
                        x1Field,
                        value,
                        x1FieldDatePrecision: settings.x1FieldDatePrecision,
                        x1FieldType: x1FieldDefinition?.dataType,
                      }),
                      dashboardTagNames
                    );
                    navigate(
                      customDatasourceUrl(selectedCustomDatasource.Id, {
                        filtering,
                      })
                    );
                  }
                }}
                reportData={(data?.reportingData as ReportField[][]) ?? []}
                chartType={settings.chartType}
                seriesData={seriesData}
                pageSize={pageSize}
                currentPageIndex={pageIndex}
                loading={loading}
                xAxisDataType={xAxisDataType}
                xAxisDatePrecision={settings.x1FieldDatePrecision ?? null}
                innerMetricValue={total?.toString()}
                columnWidthScope={
                  widgetId ? buildColumnWidthScope.widget(widgetId) : undefined
                }
                tablePreferences={settings.tablePreferences}
                onTablePreferencesChange={(preferences) => {
                  // Page size drives the server-side limit/offset, so changing it
                  // invalidates the current page index — otherwise a user on a late
                  // page lands at an offset past the end of the data.
                  //
                  // Compare *effective* sizes, matching how `pageSize` above is
                  // derived: an unset stored preference already pages at
                  // DEFAULT_PAGE_SIZE, so explicitly picking that same value is not
                  // a change and must not reset the page.
                  const previousPageSize =
                    settings.tablePreferences?.pageSize ?? DEFAULT_PAGE_SIZE;
                  const nextPageSize =
                    preferences.pageSize ?? DEFAULT_PAGE_SIZE;
                  if (nextPageSize !== previousPageSize) {
                    setPageIndex(0);
                  }
                  setSettings({ ...settings, tablePreferences: preferences });
                }}
                showAsPercentage={settings.showAsPercentage}
                invertBarChartAxis={settings.invertBarChartAxis}
                unit={settings.customUnit ? settings.unit : undefined}
              />
              {settings.showFilters !== false &&
                customDatasourceModel &&
                // Gate on tokenGroups — that's what combinedFilters() applies.
                (settings.filtering?.tokenGroups?.length ?? 0) > 0 && (
                  <div className={'print:hidden'}>
                    <CollapsibleFilterPreview
                      tokenCount={settings.filtering?.tokenGroups?.length ?? 0}
                      isCompact={isCompact}
                    >
                      <CustomDatasourcePropertyFilter
                        disabled
                        query={settings.filtering ?? emptyFilterQuery}
                        onChange={() => null}
                        allFields={customDatasourceModel.fields}
                        datasources={selectedCustomDatasource.Datasources}
                      />
                    </CollapsibleFilterPreview>
                  </div>
                )}
            </>
          )}
      </WidgetContentErrorBoundary>
    </div>
  );
};

CustomDataSourceWidget.displayName = 'CustomDataSourceWidget';
